import React, { useEffect } from 'react'
import styled from 'styled-components'
import { fancyBoxReady } from 'helpers/fancybox'
import Skeleton from 'react-loading-skeleton'
import { SliderFlickity, BtnPlay } from 'components/base-ui'

import { MediaTypeEnum, getTypeImageFromExtension } from 'helpers/image'
import { ScheduledContentTypeEnum } from 'graphQL/hatostore/generate/operations'
import { ScheduledContentType } from 'types'
import { ListScheduledContentsQuery as ListScheduledContentsQueryDineIn } from 'graphQL/dinein/generate/operations'

interface SliderListScheduledContentProps {
  listScheduledContent: ScheduledContentType[] | ListScheduledContentsQueryDineIn['listScheduledContents']
  onClickBanner: (
    scheduleContent: ScheduledContentType | ListScheduledContentsQueryDineIn['listScheduledContents'][number],
    index?: number
  ) => void
}

const SliderListScheduledContent = ({ listScheduledContent, onClickBanner }: SliderListScheduledContentProps) => {
  useEffect(() => {
    fancyBoxReady(() => {
      window.$('[data-fancybox="gallery"]').fancybox({
        loop: true,
        video: {
          tpl:
            '<video class="fancybox-video" controls controlsList="nodownload" allowfullscreen="true" poster="{{poster}}">' +
            '<source src="{{src}}" type="{{format}}" />' +
            'Sorry, your browser doesn\'t support embedded videos, <a href="{{src}}">download</a> and watch with your favorite video player!' +
            '</video>',
          format: '',
          autoStart: true,
        },
        buttons: ['close'],
        clickContent: handleClickFancyBoxContent,
        mobile: {
          clickContent: handleClickFancyBoxContent,
          clickSlide: function (current, event) {
            return 'close'
          },
        },
      })
    })
  }, [])

  return (
    <CoverWrapper>
      <WrapperSkeleton>
        <CoverSkeleton />
      </WrapperSkeleton>

      <SlideWrapper>
        <SliderFlickity
          options={{
            pageDots: listScheduledContent.length > 1,
            prevNextButtons: false,
            autoPlay: 5000,
          }}
        >
          {listScheduledContent.map((scheduleContent, i) => {
            const dataFancyBox = getDataFancyBox(scheduleContent.type)
            const { coverPath, href, isVideoType } = getMediaPaths(scheduleContent.images)

            return (
              <Banner
                key={i}
                {...dataFancyBox}
                href={href}
                video={isVideoType}
                data-type={isVideoType ? 'video' : 'image'}
                onClick={(e) => onClickBanner(scheduleContent, i)}
              >
                <img alt="banner" src={coverPath} />
                {isVideoType && (
                  <BtnPlayWrapper>
                    <BtnPlay />
                  </BtnPlayWrapper>
                )}
              </Banner>
            )
          })}
        </SliderFlickity>
      </SlideWrapper>
    </CoverWrapper>
  )

  function getMediaPaths(images: ScheduledContentType['images']) {
    const thumbnailImage = images.find((image) => image.type === 'Horizontal')
    if (thumbnailImage) {
      const { originalURL, resizableURL } = thumbnailImage
      const type = getTypeImageFromExtension(originalURL)
      const isVideoType = type === MediaTypeEnum.VIDEO
      if (isVideoType) {
        const coverImage = images.find((image) => image.type === 'HorizontalThumbnail')
        const coverPath = coverImage?.resizableURL?.replace('{width}', '780').replace('{height}', '332_fmt-jpg') || ''
        const href = originalURL
        return { coverPath, href, isVideoType }
      } else {
        const coverPath = resizableURL.replace('{width}', '780').replace('{height}', '332_fmt-jpg')
        const href = originalURL
        return { coverPath, href, isVideoType }
      }
    } else {
      return { coverPath: '', href: '', isVideoType: false }
    }
  }

  function getDataFancyBox(contentType: ScheduledContentTypeEnum) {
    if (contentType === ScheduledContentTypeEnum.SingleMedia) {
      return { 'data-fancybox': 'gallery' }
    }
    return {}
  }

  function handleClickFancyBoxContent(current, event) {
    if (event) {
      onClickBanner(listScheduledContent[current.index])
    }
  }
}

export default SliderListScheduledContent

const CoverWrapper = styled.div`
  width: 100%;
  position: relative;

  &::before {
    content: '';
    display: block;
    padding-top: 39.5%;
  }
`

const WrapperSkeleton = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  bottom: 0;
`

const CoverSkeleton = styled(Skeleton)`
  &::before {
    content: '';
    display: block;
    padding-top: 39.5%;
  }
`

const SlideWrapper = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  bottom: 0;
  z-index: 1;
  background-color: white;

  .flickity-page-dots {
    bottom: initial;
    top: 3px;

    & .dot {
      width: 8px;
      height: 8px;
      margin: 0 2px;
      background: #d9d9d9;
    }
  }
`

const Banner = styled.div<{ href?: string; video?: boolean }>`
  width: calc(100% - 30px);
  margin-right: ${({ theme }) => theme.spacing.S};
  border-radius: ${({ theme }) => theme.radius.md};
  overflow: hidden;
  z-index: 1;

  &:first-child {
    margin-left: 15px;
  }

  &::before {
    content: '';
    display: block;
    padding-top: 42.5%;
  }

  img {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
  }
`

const BtnPlayWrapper = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1;
`
